// LinkEmbed — Discord-style rich preview for a YouTube/Spotify link in a chat message.
// Fetches normalized metadata from the server oEmbed proxy (/api/embed), shows a thumbnail +
// title + channel card, and plays inline (iframe) on click. Renders nothing for non-previewable
// links or while the (fast, cached) lookup is in flight, so plain links never flash a skeleton.
import { useEffect, useState } from "react";

// Module-level cache so the same URL isn't re-fetched per render/message. Value: preview | null;
// while in flight the entry holds the Promise.
const cache = new Map();
function fetchPreview(url) {
  const existing = cache.get(url);
  if (existing !== undefined) return existing instanceof Promise ? existing : Promise.resolve(existing);
  const p = fetch(`/api/embed?url=${encodeURIComponent(url)}`, { credentials: "same-origin" })
    .then((r) => (r.status === 204 ? null : r.ok ? r.json() : null))
    .then((v) => { cache.set(url, v); return v; })
    .catch(() => { cache.set(url, null); return null; });
  cache.set(url, p);
  return p;
}

const PlayIcon = () => (
  <span className="lembed__play" aria-hidden><svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg></span>
);

// A direct link to a video file (…/clip.mp4, .webm, .mov, …) — with or without a query/fragment.
export function isVideoUrl(u) {
  return /\.(mp4|webm|mov|m4v|ogv|ogg)(?:$|[?#])/i.test(u || "");
}

export default function LinkEmbed({ url }) {
  const video = isVideoUrl(url);
  const cached = cache.get(url);
  const [preview, setPreview] = useState(cached && !(cached instanceof Promise) ? cached : undefined);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (video) return; // direct video file — no oEmbed lookup needed
    let live = true;
    fetchPreview(url).then((v) => { if (live) setPreview(v ?? null); });
    return () => { live = false; };
  }, [url, video]);

  // A pasted video-file link plays inline in a native player.
  if (video) {
    return (
      <div className="lembed lembed--video">
        <video src={url} className="msg__video" controls preload="metadata" onClick={(e) => e.stopPropagation()} />
      </div>
    );
  }

  if (!preview) return null; // undefined (loading) or null (not previewable)

  // Spotify's own embed is already a compact, rich, non-autoplaying player (art + title + artist +
  // play) — render it directly rather than a cropped square thumbnail.
  if (preview.provider === "spotify") {
    const tall = preview.kind !== "track" && preview.kind !== "episode";
    return (
      <div className="lembed lembed--spotify lembed--playing">
        <div className={`lembed__frame ${tall ? "lembed__frame--sp-tall" : "lembed__frame--sp"}`}>
          <iframe src={preview.embedUrl} title={preview.title || "Spotify"} loading="lazy"
            allow="autoplay; encrypted-media; clipboard-write; fullscreen; picture-in-picture" />
        </div>
      </div>
    );
  }

  // YouTube: a light card (thumbnail + channel + title), plays inline on click.
  if (playing) {
    return (
      <div className="lembed lembed--youtube lembed--playing">
        <div className="lembed__frame lembed__frame--yt">
          <iframe src={preview.embedUrl} title={preview.title || "YouTube"} loading="lazy"
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen />
        </div>
      </div>
    );
  }
  return (
    <div className="lembed lembed--youtube">
      <span className="lembed__provider lembed__provider--youtube">YouTube</span>
      {preview.author && <div className="lembed__author">{preview.author}</div>}
      {preview.title && <a className="lembed__title" href={preview.url} target="_blank" rel="noreferrer noopener" onClick={(e) => e.stopPropagation()}>{preview.title}</a>}
      <button type="button" className="lembed__thumb" onClick={() => setPlaying(true)} aria-label={`Play ${preview.title || "video"}`}>
        {preview.thumbnail
          ? <img src={preview.thumbnail} alt="" loading="lazy"
              onError={(e) => { e.currentTarget.src = `https://i.ytimg.com/vi/${preview.id}/mqdefault.jpg`; }} />
          : <div className="lembed__thumb-blank" />}
        <PlayIcon />
      </button>
    </div>
  );
}

// URLs in a message body that we render as rich embeds (YouTube / Spotify). Trailing punctuation
// is trimmed; at most two per message so a link-heavy message doesn't balloon.
const URL_RE = /(https?:\/\/[^\s<]+)/g;
export function embeddableUrls(text) {
  if (!text) return [];
  const out = [];
  const matches = text.match(URL_RE) || [];
  for (let u of matches) {
    u = u.replace(/[.,!?;:)\]}'"]+$/, "");
    const yt = /(?:youtube\.com\/(?:watch\?|shorts\/|embed\/|live\/)|youtu\.be\/)/i.test(u);
    const sp = /open\.spotify\.com\/(?:intl-[a-z]{2}\/)?(?:track|album|playlist|episode|show)\//i.test(u);
    if ((yt || sp || isVideoUrl(u)) && !out.includes(u)) out.push(u);
  }
  return out.slice(0, 2);
}
